import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Provider } from 'jotai';
import { StrictMode } from 'react';
import { PropsWithChildren } from 'react';
import { createRoot } from 'react-dom/client';

import { store } from '@/lib/store';
import { initializeSipLogging } from '@/lib/sip-logging';

import { env } from './config/env';
import { useAppInitialization } from './hooks/use-app-initialization';
import './index.css';
import './lib/i18n';
import { routeTree } from './routeTree.gen';

initializeSipLogging();

const JotaiProvider = ({ children }: PropsWithChildren) => (
  <Provider store={store}>{children}</Provider>
);

const router = createRouter({ routeTree });

const queryClient = new QueryClient();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <JotaiProvider>
        <Sentry.ErrorBoundary
          fallback={<div>Something went wrong. Check console.</div>}
        >
          <App />
        </Sentry.ErrorBoundary>
      </JotaiProvider>
    </QueryClientProvider>
  </StrictMode>
);

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

if (env.VITE_SENTRY_ENABLED === 'true') {
  Sentry.init({
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.captureConsoleIntegration(),
    ],
    dsn: env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    sampleRate: env.VITE_SENTRY_SAMPLE_RATE,
    replaysOnErrorSampleRate: env.VITE_SENTRY_SAMPLE_RATE,
  });
}

function App() {
  useAppInitialization();
  return <RouterProvider router={router} />;
}

export default App;
